import React, { FunctionComponent, useEffect, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import InputField from "components/textField/InputField";
import Button from "components/button/Button";
import Styles from "views/register/Register.module.scss";
import getItemFromLocalStorage from "utils/getItemFromLocalStorage";
import { checkEmail } from "utils/FormValidation";
import LoginLeftSection from "components/loginLeftSection/LoginLeftSection";
import { trackFormInteraction } from "utils/clarity";
import { useSelector } from "react-redux";
import { User } from "modules/user/types";
import { getUser } from "store/selector/auth.selector";

interface LoginProps {
  isLoading: boolean;
  authError: any;
  loginRequest: Function;
}

const Login: FunctionComponent<LoginProps> = (props: LoginProps) => {
  const { isLoading, loginRequest, authError } = props;
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [emailError, setEmailError] = useState<string>("");
  const [emptyFields, setEmptyFields] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const history = useHistory();
  const location = useLocation<{ from?: string }>();
  const redirectTo = location.state?.from || "/dashboard";
  const userToken: User = useSelector(getUser);
  const user: any = localStorage.getItem("user");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("reset") === "1") {
      setResetSuccess(true);
      params.delete("reset");
      const nextSearch = params.toString();
      history.replace({
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
        state: location.state,
      });
    }
  }, [history, location.pathname, location.search, location.state]);

  useEffect(() => {
    if (user && JSON.parse(user).access) {
      history.push(redirectTo);
    } else {
      trackFormInteraction("login", "opened");
    }
  }, [user]);

  const handleLogin = () => {
    if (email.length === 0 || password.length === 0) {
      setEmptyFields(true);
      trackFormInteraction("login", "validation_error", "empty_fields");
      return;
    } else {
      setEmptyFields(false);
    }
    isButtonEnabled();
    if (
      email.length < 1 ||
      password.length < 1 ||
      emailError.length > 0 ||
      isLoading
    ) {
      return;
    }

    const user = getItemFromLocalStorage("user");
    if (user === null) {
      loginRequest({ email, password });
    } else {
      history.go(0);
    }
  };

  const handleKeyDownEvent = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      handleLogin();
    }
  };

  const isButtonEnabled = () => {
    setEmailError(checkEmail(email));
  };

  const handleBlur = (event: React.FormEvent<HTMLInputElement>) => {
    const val = event.currentTarget.value;
    setEmailError(checkEmail(val));
  };

  return (
    <div className={Styles.customContainer}>
      <div className={Styles.leftSectionWrapper}>
        <LoginLeftSection />
      </div>
      <div className={Styles.rightSectionWrapper}>
        <div className={Styles.loginArea}>
          <div className={Styles.formContent}>
            <h2 className={Styles.loginPageTitle}>Login to your account</h2>
            <div className={Styles.loginPageSubtitle}>Welcome back!</div>

            {resetSuccess && (
              <p className="mb-3" style={{ color: "green", textAlign: "left" }}>
                Password changed successfully. Please log in.
              </p>
            )}

            <div className={Styles.formWrapper}>
              <div>
                <InputField
                  errorMessage={emailError}
                  blurHandler={handleBlur}
                  handleChange={setEmail}
                  type="email"
                  placeholder="Email Address"
                />
                {emptyFields && (
                  <p className={Styles.error}>Please fill all fields</p>
                )}
              </div>
              <div>
                <InputField
                  handleKeyDown={handleKeyDownEvent}
                  handleChange={setPassword}
                  type="password"
                  placeholder="Password"
                />
                {authError && <p className={Styles.error}>{authError}</p>}
              </div>
            </div>

            <div className={Styles.forgotLink}>
              <Link to="/forgot-password">Forgot password?</Link>
            </div>

            <div className={Styles.btnBlock}>
              <Button
                showLoading={isLoading}
                clickHandler={handleLogin}
                label="LOG IN"
                cssClass={Styles.authPrimaryBtn}
              />
            </div>

            <div className={Styles.bottomText}>
              Don&apos;t have an account?{" "}
              <Link to="/registration">CREATE AN ACCOUNT</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
